<template>
  <div>
    <DefaultIntroductionMessageHeader
      @edit="handleEdit"
      @save="handleSave"
      @cancel="handleCancel"
      :isEditing="isEditing"
      :isSaveButtonDisabled="isSubmitDisabled"
      :hasIntroductionMessage="!!introductionMessageContent"
    />
    <section v-if="!isEditing" :class="$style.content">
      <div v-if="!introductionMessageContent">
        <div :class="$style.noIntroductionMessageCard">
          <OkanekoIntroductionNoMessageImage
            :class="$style.noIntroductionMessageCard__image"
          />
          <p>初期チャットメッセージが作成されていません。</p>
          <p>
            あなたとマッチングしたユーザーとのチャットで最初に送られる挨拶メッセージを作成しましょう。
          </p>
          <FlatButton
            :class="$style.noIntroductionMessageCard__button"
            variant="primary"
            @click="handleEdit"
          >
            ＋ 新規作成
          </FlatButton>
        </div>
      </div>
      <div v-else>
        <div
          v-if="
            introductionMessageContent.__typename ===
            'PlainIntroductionMessageContent'
          "
        >
          <p :class="$style.title">設定中のメッセージ</p>
          <p :class="[$style.content__body, $style.body]">
            {{ introductionMessageContent.body }}
          </p>
          <ul :class="[$style.content__selectionList, $style.selectionList]">
            <li
              :class="[$style.selectionList__listItem, $style.listItem]"
              v-for="(
                selection, index
              ) in introductionMessageContent.selections"
              :key="index"
            >
              {{ selection }}
            </li>
          </ul>
        </div>
        <div
          v-else-if="
            introductionMessageContent.__typename ===
            'ScenarioChatIntroductionMessageContent'
          "
        >
          <p>
            シナリオチャット[
            <RouterLink
              :to="{
                name: 'scenarioChatEdit',
                params: {
                  entranceGUID: route.params.entranceGUID,
                  guid: introductionMessageContent.scenarioChatGuid,
                },
              }"
              ><strong>{{ scenarioChatTitle }}</strong></RouterLink
            >
            ]が設定されています
          </p>
        </div>
      </div>
    </section>
    <section v-else>
      <IntroductionMessageContentForm
        v-if="contentForm && scenarioChatOptions"
        :scenarioChatOptions="scenarioChatOptions"
        :hasLineActivationProvider="hasLineActivationProvider"
        v-model="contentForm"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { useAlert } from '@/libs/alertPortal'
import { isClientError } from '@/libs/utilities/CustomErrors'
import { captureException } from '@sentry/vue'
import { computed, ref, watchEffect, watch } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'

import OkanekoIntroductionNoMessageImage from '@/assets/images/okaneko-introduction-no-message.svg?component'
import FlatButton from '@/components/atoms/FlatButton.vue'
import DefaultIntroductionMessageHeader from './DefaultIntroductionMessageHeader.vue'
import { useSaveDefaultIntroductionMessageMutation } from './SettingsPageIntroductionMessageView.generated'
import { useFetchScenarioChatsQuery } from './IntroductionMessageForm.generated'
import IntroductionMessageContentForm from './IntroductionMessageContentForm.vue'
import {
  validateIntroductionMessageContentForm,
  type IntroductionMessageContentFormType,
} from './formSchema'
import { useLogger } from '@/logger/logger'

const props = defineProps<{
  introductionMessageContent:
    | {
        __typename: 'PlainIntroductionMessageContent'
        body: string
        selections: Array<string>
      }
    | {
        __typename: 'ScenarioChatIntroductionMessageContent'
        scenarioChatGuid: string
      }
    | null
    | undefined
  hasLineActivationProvider: boolean
}>()

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: {
      onConfirm: () => void
      onAbort: () => void
    }
  ): void
  (event: 'doneSave'): void
}>()

const initialContentFormValue = computed(() => {
  if (
    props.introductionMessageContent?.__typename ===
    'PlainIntroductionMessageContent'
  ) {
    return {
      type: 'plain' as const,
      body: props.introductionMessageContent.body,
      selections: props.introductionMessageContent.selections,
    }
  } else if (
    props.introductionMessageContent?.__typename ===
    'ScenarioChatIntroductionMessageContent'
  ) {
    // シナリオチャットをリリースしたらこれをデフォルトとして扱う
    return {
      type: 'scenarioChat' as const,
      scenarioChatGuid:
        props.introductionMessageContent?.scenarioChatGuid ?? '',
    }
  }
  return {
    type: 'plain' as const,
    body: '',
    selections: [],
  }
})

const { result, refetch } = useFetchScenarioChatsQuery()

const scenarioChatOptions = computed(() => {
  return result.value?.scenarioChats.scenarioChats.map(chat => ({
    text: chat.title,
    value: chat.guid,
  }))
})

const route = useRoute('introductionMessageSetting')
const entranceGUID = computed(() => route.params.entranceGUID)
watch(
  entranceGUID,
  () => {
    refetch()
  },
  { immediate: true }
)

const contentForm = ref<IntroductionMessageContentFormType>()

watchEffect(() => {
  contentForm.value = initialContentFormValue.value
})

const scenarioChatTitle = computed(() => {
  return result.value?.scenarioChats.scenarioChats.find(
    chat => chat.guid === initialContentFormValue.value.scenarioChatGuid
  )?.title
})

// 編集モードかどうか
const isEditing = ref(false)

const isSubmitting = ref(false)
const isSubmitDisabled = computed(() => {
  if (!contentForm.value) {
    return true
  }

  // 送信中もボタンを disable する
  return (
    !!validateIntroductionMessageContentForm(contentForm.value) ||
    isSubmitting.value
  )
})

const handleEdit = () => {
  isEditing.value = !isEditing.value
}

const alert = useAlert()
const logger = useLogger()

const { mutate: saveDefaultIntroductionMessageMutation, onDone } =
  useSaveDefaultIntroductionMessageMutation({})

const handleSave = async () => {
  if (!contentForm.value) {
    return
  }

  const input = {
    content:
      contentForm.value.type === 'scenarioChat'
        ? {
            scenarioChatIntroductionMessage: {
              scenarioChatGuid: contentForm.value.scenarioChatGuid,
            },
          }
        : {
            plainIntroductionMessage: {
              body: contentForm.value.body,
              selections: contentForm.value.selections,
            },
          },
  }
  try {
    await saveDefaultIntroductionMessageMutation({ input })
    await logger.ltlPeople.event.saveDefaultIntroductionMessage(
      logger.buildMeta(),
      {
        data: {
          content:
            contentForm.value.type === 'scenarioChat'
              ? {
                  scenarioChatGuid: contentForm.value.scenarioChatGuid,
                }
              : {
                  body: contentForm.value.body,
                  selections: contentForm.value.selections,
                },
        },
      }
    )
  } catch (error) {
    if (isClientError(error)) {
      captureException(error)
      alert.error('エラーが発生しました。')
    }
  }

  isEditing.value = !isEditing.value
}

onDone(() => {
  emit('doneSave')
  alert.success('初期チャットメッセージを更新しました。')
})

onBeforeRouteLeave((to, from, next) => {
  if (!isEditing.value) {
    next()
    return
  }
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      next()
    },
    onAbort: () => {},
  })
})

const handleCancel = () => {
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      contentForm.value = initialContentFormValue.value
      isEditing.value = false
    },
    onAbort: () => {},
  })
}
</script>

<style lang="scss" module>
.noIntroductionMessageCard {
  background-color: $color-background-base;
  border-radius: $border-radius-v2-base;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;

  &__image {
    max-width: 100%;
    margin-bottom: 16px;
  }

  &__button {
    margin-top: 16px;
  }
}

.title {
  font-size: 14px;
  font-weight: 700;
}

.body {
  white-space: pre-wrap;

  &:nth-child(n + 2) {
    margin-top: 16px;
  }
}

.selectionList {
  &:nth-child(n + 2) {
    margin-top: 12px;
  }
}

.listItem {
  background-color: $color-background-base;
  border-radius: $border-radius-v2-base;
  padding: 16px;

  &:nth-child(n + 2) {
    margin-top: 8px;
  }
}
</style>
